import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import { formatPrice } from "../../../../utilities/formatPrice";
import ExtraPayService from "../../../../service/ExtraPayService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getAdminUsername, getToken, normalizeText } from "../../utils/adminUtils";
import StatusTabs from "../../components/list/StatusTabs";
import SearchInput from "../../components/list/SearchInput";
import StatusBadge from "../../components/list/StatusBadge";
import EmptyState from "../../components/list/EmptyState";
import CopyValue from "../../components/list/CopyValue";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";

const TABS = {
  PENDING: "REQUESTED",
  COMPLETED: "COMPLETED",
  ALL: "all",
};

// A API pode devolver o id em campos diferentes, como tratado no admin clássico
const getWithdrawId = (row) => row?.id ?? row?.idwalletTokensWithdrawRequest;

const exc = (value) => `EXC ${formatPrice(Number(value) || 0)}`;

const loadExcWithdrawRequests = async () => {
  const res = await new ExtraPayService(getToken()).listAllWithdrawals(getAdminUsername());
  const result = expectCode(res, 200001).result;
  return (Array.isArray(result) ? result : []).map((row) => ({ ...row, id: getWithdrawId(row) }));
};

const AdminExcWithdrawRequests = () => {
  const { items, loading, loadError, reload } = useAdminList(loadExcWithdrawRequests);
  const { run, runningId } = useAdminAction();
  const [tab, setTab] = useState(TABS.PENDING);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        amountNumber: Number(row.amount) || 0,
        searchText: buildSearchText([row.username, row.email, row.addressEXC]),
      })),
    [items],
  );

  const counts = useMemo(
    () => ({
      [TABS.PENDING]: rows.filter((row) => row.status === TABS.PENDING).length,
      [TABS.COMPLETED]: rows.filter((row) => row.status === TABS.COMPLETED).length,
      [TABS.ALL]: rows.length,
    }),
    [rows],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    return rows.filter(
      (row) =>
        (tab === TABS.ALL || row.status === tab) && (!term || row.searchText.includes(term)),
    );
  }, [rows, tab, search]);

  const processSelected = async () => {
    const ok = await run(
      selected.id,
      async () =>
        expectCode(
          await new ExtraPayService(getToken()).processWithdrawal({
            idwalletTokensWithdrawRequest: selected.id,
            useradmin: { username: getAdminUsername() },
          }),
          200001,
        ),
      "excWithdrawProcessedSuccessfully",
    );
    setSelected(null);
    if (ok) reload();
  };

  const statusTemplate = (row) => {
    if (row.status === TABS.COMPLETED) {
      return <StatusBadge tone="good">{localeText("completed")}</StatusBadge>;
    }
    if (row.status === TABS.PENDING) {
      return <StatusBadge tone="warning">{localeText("extrapayFlowStatusRequested")}</StatusBadge>;
    }
    return <StatusBadge tone="neutral">{row.status || "—"}</StatusBadge>;
  };

  const hasFilters = !!search.trim();

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!selected}
        message={localeText("processThisExcWithdrawRequest")}
        details={
          selected
            ? [
                { label: localeText("username"), value: selected.username && `@${selected.username}` },
                { label: localeText("email"), value: selected.email },
                { label: localeText("extrapayFlowWalletAddress"), value: selected.addressEXC },
                { label: localeText("amountRequested"), value: exc(selected.amount) },
              ]
            : []
        }
        confirmLabel={localeText("markExcWithdrawCompleted")}
        loading={!!selected && runningId === selected.id}
        onConfirm={processSelected}
        onCancel={() => setSelected(null)}
      />

      <StatusTabs
        tabs={[
          { value: TABS.PENDING, label: localeText("extrapayFlowStatusRequested") },
          { value: TABS.COMPLETED, label: localeText("completed") },
          { value: TABS.ALL, label: localeText("adminUsersStatusAll") },
        ]}
        value={tab}
        onChange={setTab}
        counts={counts}
        loading={loading}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("searchByUsernameEmailAddress")}
          />
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminUsersResults")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="dateRequest"
            sortOrder={-1}
            removableSort
            scrollable
            tableStyle={{ minWidth: "64rem" }}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon={tab === TABS.PENDING && !hasFilters ? "pi pi-check-circle" : "pi pi-inbox"}
                  message={localeText(
                    hasFilters
                      ? "adminListNoResults"
                      : tab === TABS.PENDING
                        ? "adminWithdrawNoPending"
                        : "adminListEmpty",
                  )}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={() => setSearch("")}
                />
              )
            }
          >
            <Column
              header={localeText("adminUsersUser")}
              field="username"
              body={(row) => (
                <div className="admin-stack">
                  <span className="admin-strong">{row.username ? `@${row.username}` : "—"}</span>
                  {row.email && <span className="admin-muted">{row.email}</span>}
                </div>
              )}
              sortable
            />
            <Column
              header={localeText("amountRequested")}
              field="amountNumber"
              body={(row) => <span className="admin-num admin-strong">{exc(row.amount)}</span>}
              sortable
            />
            <Column
              header={localeText("extrapayFlowWalletAddress")}
              body={(row) => <CopyValue value={row.addressEXC} mono />}
            />
            <Column
              header={localeText("requestDate")}
              field="dateRequest"
              body={(row) => formatDay(row.dateRequest)}
              sortable
            />
            <Column header={localeText("status")} field="status" body={statusTemplate} sortable />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(row) =>
                row.status === TABS.PENDING ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn--primary"
                    onClick={() => setSelected(row)}
                    disabled={runningId === row.id}
                  >
                    <i
                      className={runningId === row.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                      aria-hidden="true"
                    />
                    {localeText("markExcWithdrawCompleted")}
                  </button>
                ) : null
              }
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminExcWithdrawRequests;
